import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * Deferred pagination controller for <results-list>.
 * The host remains a Component so its refs keep tracking Section Rendering DOM changes,
 * while pagination-only dependencies can stay off the initial Collection/Search path.
 */
export default class PaginatedListController {
  /** @param {import('@theme/component').Component} host */
  constructor(host) {
    this.host = host;
  }

  /** @type {import('@theme/component').Component} */
  host;

  /** @type {Map<number, string>} */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  #connected = false;

  // Thin aliases retained for compatibility with the previous lifecycle-shaped API.
  // They do not make this controller a custom-element superclass again.
  connectedCallback() {
    this.connect();
  }

  disconnectedCallback() {
    this.disconnect();
  }

  connect() {
    if (this.#connected) return;
    this.#connected = true;
    this.#refreshAspectRatioHelper();
    this.#observeViewMore();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnect() {
    if (!this.#connected) return;
    this.#connected = false;
    this.infinityScrollObserver?.disconnect();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Refresh refs after Section Rendering updates the host subtree.
   * No page request starts here unless a valid boundary is already within 600px.
   */
  refresh() {
    if (!this.#connected || !this.host.isConnected) return;
    this.#refreshAspectRatioHelper();
    this.infinityScrollObserver?.disconnect();
    this.#observeViewMore();
  }

  #refreshAspectRatioHelper() {
    const templateCard = this.host.querySelector('[ref="cardGallery"]');
    this.#aspectRatioHelper = templateCard
      ? new PaginatedListAspectRatioHelper({ templateCard })
      : undefined;
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.host.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const { viewMorePrevious, viewMoreNext } = this.host.refs;
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === viewMoreNext) {
              this.#renderNextPage();
            }
          }
        },
        {
          // Preserve the existing page-request threshold. The lightweight host loader
          // imports this controller earlier, but HTML is still not fetched until 600px.
          rootMargin: '600px',
        }
      );
    }

    if (viewMorePrevious instanceof Element) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext instanceof Element) this.infinityScrollObserver.observe(viewMoreNext);
  }

  /** @param {{ page: number, url?: URL } | undefined} pageInfo */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const lastPage = this.host.refs.grid?.dataset.lastPage;
    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;
    return true;
  }

  /** @param {'previous' | 'next'} type */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /** @param {number} pageNumber @param {URL} [url] */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.host.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);
    if (!nextPageItemElements) {
      const promise = new Promise((resolve) => {
        this.#resolveNextPagePromise = resolve;
      });

      this.#fetchPage('next');
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);
    this.#notifyPageReveal(grid, nextPage.page, 'next');
    this.#aspectRatioHelper?.processNewElements();
    await yieldToMainThread();
    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => this.#fetchPage('next'));
  }

  async #renderPreviousPage() {
    const { grid } = this.host.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((resolve) => {
        this.#resolvePreviousPagePromise = resolve;
      });

      this.#fetchPage('previous');
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    grid.prepend(...previousPageItemElements);
    this.#notifyPageReveal(grid, previousPage.page, 'previous');
    this.#aspectRatioHelper?.processNewElements();

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({ top: currentScrollTop + heightDiff, behavior: 'instant' });
    }

    await yieldToMainThread();
    history.pushState('', '', previousPage.url.toString());
    requestIdleCallback(() => this.#fetchPage('previous'));
  }

  /** @param {HTMLElement} grid @param {number} page @param {'previous' | 'next'} direction */
  #notifyPageReveal(grid, page, direction) {
    grid.dispatchEvent(
      new CustomEvent('kervelan:collection-page-change', {
        bubbles: true,
        detail: { page, direction },
      })
    );
  }

  /** @param {'previous' | 'next'} type */
  #getPage(type) {
    const { cards } = this.host.refs;
    if (!Array.isArray(cards)) return;

    const isPrevious = type === 'previous';
    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /** @param {number} page */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.host.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;

    this.pages.clear();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentGrid = this.host.refs.grid;
    const currentLastPage = currentGrid?.dataset.lastPage;
    const observer = new MutationObserver(() => {
      const newLastPage = this.host.refs.grid?.dataset.lastPage;
      if (newLastPage === currentLastPage) return;

      observer.disconnect();
      if (this.host.isConnected) this.refresh();
    });

    if (currentGrid) {
      observer.observe(currentGrid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => observer.disconnect(), 3000);
    }
  };
}
